import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentGame } from '@/store/gameSlice';
import { selectUser } from '@/store/authSlice';
import type { DevelopmentTimer } from '@/types';
import './DevelopmentPhaseOverlay.css';

/**
 * Get human-readable label for AI action
 */
function getAIActionLabel(action: string | null, actionData: any): string {
  if (!action) return 'Thinking...';
  
  const outfitType = actionData?.outfitType?.toUpperCase() || 'ROVER';
  
  switch (action) {
    case 'entering_store':
      return '🚶 Walking to store...';
    case 'buying_rover':
      return '🛒 Buying rover...';
    case 'outfitting':
      return `🔧 Outfitting with ${outfitType}...`;
    case 'exiting_store':
      return '🚪 Exiting store...';
    case 'walking_to_plot':
      return '🚶 Walking to plot...';
    case 'deploying':
      return `🚀 Deploying ${outfitType} rover!`;
    case 'done':
      return '✅ Turn complete!';
    case 'skipped':
      return '⏭️ Skipping turn...';
    default:
      return 'Thinking...';
  }
}

interface DevelopmentPhaseOverlayProps {
  onMyTurnStart?: () => void;
}

/**
 * DevelopmentPhaseOverlay - Shows turn indicator during development phase
 * 
 * Displays:
 * - Current player's turn (with avatar icon and name)
 * - Timer countdown
 * - Turn order progress (e.g., "Turn 2 of 4")
 * - "YOUR TURN" indicator when it's the current user's turn
 */
export default function DevelopmentPhaseOverlay({ onMyTurnStart }: DevelopmentPhaseOverlayProps) {
  const game = useSelector(selectCurrentGame);
  const user = useSelector(selectUser);
  const [lastNotifiedPlayer, setLastNotifiedPlayer] = useState<string | null>(null);
  const timer: DevelopmentTimer | null | undefined = game?.developmentTimer;
  const [shownRemaining, setShownRemaining] = useState(timer?.timeRemaining || 0);
  const clockAnchor = useRef({ remaining: timer?.timeRemaining || 0, at: Date.now() });

  useEffect(() => {
    clockAnchor.current = { remaining: timer?.timeRemaining || 0, at: Date.now() };
    setShownRemaining(timer?.timeRemaining || 0);
  }, [timer?.timeRemaining, timer?.turnStartTime]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const left = clockAnchor.current.remaining - (Date.now() - clockAnchor.current.at) / 1000;
      setShownRemaining(Math.max(0, left));
    }, 200);
    return () => window.clearInterval(id);
  }, []);

  // Check if it's current user's turn
  const currentPlayer = game?.players?.find(p => p.userId === user?.userId || p.playerId === user?.userId);
  const isMyTurn = timer?.currentPlayerId === currentPlayer?.playerId;

  // Notify when it becomes user's turn
  useEffect(() => {
    if (isMyTurn && timer?.currentPlayerId && timer.currentPlayerId !== lastNotifiedPlayer) {
      setLastNotifiedPlayer(timer.currentPlayerId);
      onMyTurnStart?.();
    }
  }, [isMyTurn, timer?.currentPlayerId, lastNotifiedPlayer, onMyTurnStart]);

  // Don't render if not in development phase or no timer
  if (!game || game.phase !== 'development' || !timer || !timer.active) {
    return null;
  }

  // Calculate progress
  const totalPlayers = timer.playerTurnOrder?.length || 0;
  const completedTurns = timer.completedTurns?.length || 0;
  const currentTurnNumber = completedTurns + 1;

  // Get player color for styling
  const activePlayer = game.players?.find(p => p.playerId === timer.currentPlayerId);
  const playerColor = activePlayer?.color || '#00FFFF';
  const playerNumber = timer.currentPlayerNumber || 1;

  // Format time remaining
  const timeRemaining = Math.ceil(shownRemaining);
  const isLowTime = timeRemaining <= 10;

  return (
    <div className="development-overlay">
      <div className="development-banner" style={{ borderColor: playerColor }}>
        {/* Turn indicator */}
        <div className="turn-indicator">
          <span className="turn-label">DEVELOPMENT PHASE</span>
          <span className="turn-progress">Turn {currentTurnNumber} of {totalPlayers}</span>
        </div>

        {/* Current player info */}
        <div className="current-player-info">
          {isMyTurn ? (
            <div className="your-turn-indicator">
              <span className="your-turn-icon">🎮</span>
              <span className="your-turn-text">YOUR TURN!</span>
            </div>
          ) : (
            <div className="other-player-turn">
              <div 
                className={`player-avatar player-${playerNumber}`}
                style={{ backgroundColor: playerColor }}
              >
                {timer.currentPlayerIsAI ? '🤖' : `P${playerNumber}`}
              </div>
              <div className="player-details">
                <span className="player-name">{timer.currentPlayerName || 'Unknown'}</span>
                {timer.currentPlayerIsAI && timer.aiAction && (
                  <span className="ai-action-status">
                    {getAIActionLabel(timer.aiAction, timer.aiActionData)}
                  </span>
                )}
                {!timer.currentPlayerIsAI && (
                  <span className="player-type">Human Player</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Timer */}
        <div className={`timer-display ${isLowTime ? 'low-time' : ''}`}>
          <span className="timer-icon">⏱️</span>
          <span className="timer-value">{timeRemaining}s</span>
        </div>

        {/* Progress dots */}
        <div className="turn-progress-dots">
          {timer.playerTurnOrder?.map((playerId, index) => {
            const player = game.players?.find(p => p.playerId === playerId);
            const isCompleted = timer.completedTurns?.includes(playerId);
            const isCurrent = index === timer.currentPlayerIndex;
            const pColor = player?.color || '#666';
            
            return (
              <div
                key={playerId}
                className={`progress-dot ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
                style={{ 
                  backgroundColor: isCompleted || isCurrent ? pColor : 'transparent',
                  borderColor: pColor
                }}
                title={`${player?.username || player?.playerName || `Player ${player?.playerNumber}`} ${isCompleted ? '(Done)' : isCurrent ? '(Current)' : ''}`}
              >
                {player?.isAI && <span className="ai-indicator">🤖</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Instructions when it's user's turn */}
      {isMyTurn && (
        <div className="turn-instructions">
          <p>
            <strong>Walk to the store</strong> (center hex) to buy and outfit a rover, 
            then <strong>deploy it to your owned plot</strong>!
          </p>
        </div>
      )}
    </div>
  );
}


